import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Users, RefreshCw, UserPlus, KeyRound, Power, ShieldAlert } from "lucide-react";
import {
    listUsers, createUser, updateUser, setUserPassword, changeOwnPassword,
    listEntities, listProperties,
} from "@/lib/api";
import { germanDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Seitenkopf } from "@/hilfe/Seitenkopf";
import { sperre } from "@/hilfe/Knopfhilfe";
// Dieselben vier Rollen wie im Backend und in der Nachbar-App. Die Beschreibung
// nennt, was die Rolle DARF — nicht was sie ist; „schreiben" allein sagt keinem
// Nutzer, ob er damit eine Immobilie löschen kann.
const ROLLEN = [
    { value: "lesen",     label: "Lesen",     was: "im eigenen Bereich ansehen, nichts ändern" },
    { value: "schreiben", label: "Schreiben", was: "der Alltag: Stammdaten, Mietkonto, Importe" },
    { value: "verwalten", label: "Verwalten", was: "zusätzlich löschen und Zugänge im eigenen Bereich verwalten" },
    { value: "admin",     label: "Admin",     was: "unbeschränkt: jede Entität, jede Immobilie, jeder Zugang" },
];
const ROLLE_BY = Object.fromEntries(ROLLEN.map((r) => [r.value, r]));

const RolleMerkmal = ({ rolle }) => {
    const ton = rolle === "admin" ? "text-rose-700 bg-rose-50 border-rose-200"
        : rolle === "verwalten" ? "text-violet-700 bg-violet-50 border-violet-200"
        : rolle === "schreiben" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : "text-gray-600 bg-gray-50 border-gray-200";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${ton}`}
              title={ROLLE_BY[rolle]?.was}>
            {ROLLE_BY[rolle]?.label || rolle}
        </span>
    );
};

// Was ein Zugang sieht. Zwei Ebenen, weil der Bestand zwei hat: eine Entität
// schließt alle ihre Häuser ein — auch künftige —, eine einzelne Immobilie ist
// die feinere Zuordnung. Beides zusammen ist eine Vereinigung.
const BereichWahl = ({ stamm, alle, setAlle, entitaeten, setEntitaeten,
                       immobilien, setImmobilien, darfAlle }) => {
    const um = (liste, setzen, id) => setzen(
        liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id]);
    return (
        <div className="space-y-2">
            <Label>Bereich</Label>
            {darfAlle && (
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={alle} data-testid="bereich-alle"
                           onChange={(e) => setAlle(e.target.checked)} />
                    Alle Entitäten und Immobilien — auch künftige
                </label>
            )}
            {!alle && (
                <div className="border border-gray-100 rounded-md max-h-56 overflow-y-auto divide-y divide-gray-50">
                    {(stamm.entities || []).map((e) => (
                        <div key={e.id} className="px-2 py-1.5">
                            <label className="flex items-center gap-2 text-sm font-medium">
                                <input type="checkbox" checked={entitaeten.includes(e.id)}
                                       data-testid={`bereich-entitaet-${e.id}`}
                                       onChange={() => um(entitaeten, setEntitaeten, e.id)} />
                                {e.name}
                            </label>
                            {/* Einzelne Häuser nur anbieten, solange die ganze
                                Entität nicht schon angehakt ist — sonst sieht es
                                aus, als könnte man innerhalb einer vergebenen
                                Entität noch etwas wegnehmen. Kann man nicht. */}
                            {!entitaeten.includes(e.id) && (stamm.props || [])
                                .filter((p) => p.entity_id === e.id)
                                .map((p) => (
                                    <label key={p.id} className="flex items-center gap-2 text-xs text-gray-600 ml-6 mt-0.5">
                                        <input type="checkbox" checked={immobilien.includes(p.id)}
                                               data-testid={`bereich-immobilie-${p.id}`}
                                               onChange={() => um(immobilien, setImmobilien, p.id)} />
                                        {p.address || p.label}
                                    </label>
                                ))}
                        </div>
                    ))}
                </div>
            )}
            <div className="text-[11px] text-gray-500">
                Ohne Zuordnung sieht der Zugang nichts — nicht alles. Wer eine Entität
                bekommt, sieht auch ihre künftigen Häuser; wer einzelne Häuser bekommt,
                nur diese.
            </div>
        </div>
    );
};


// ---------------------------------------------------------------------------
const NeuDialog = ({ open, onOpenChange, onDone, stamm, unbeschraenkt }) => {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [rolle, setRolle] = useState("schreiben");
    const [passwort, setPasswort] = useState("");
    const [busy, setBusy] = useState(false);
    const [alle, setAlle] = useState(false);
    const [entitaeten, setEntitaeten] = useState([]);
    const [immobilien, setImmobilien] = useState([]);

    useEffect(() => {
        if (!open) {
            setEmail(""); setName(""); setRolle("schreiben"); setPasswort("");
            setAlle(false); setEntitaeten([]); setImmobilien([]);
        }
    }, [open]);

    const speichern = async () => {
        setBusy(true);
        try {
            await createUser({
                email: email.trim(), name: name.trim(), role: rolle, password: passwort,
                alle_bereiche: alle, entitaeten, immobilien,
            });
            toast.success(`Zugang für ${email.trim()} angelegt`, {
                description: "Das Passwort persönlich weitergeben, nicht per Chat oder Mail.",
            });
            onDone();
            onOpenChange(false);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht angelegt", { description: typeof d === "string" ? d : "Eingaben prüfen." });
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Zugang anlegen</DialogTitle>
                    <DialogDescription>
                        Die Rolle entscheidet, wer ändern darf; der Bereich, was er überhaupt
                        sieht. Beides zusammen ergibt den Zugang.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label>E-Mail</Label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                               data-testid="zugang-email" />
                    </div>
                    <div>
                        <Label>Name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                               placeholder="erscheint in der Historie" data-testid="zugang-name" />
                    </div>
                    <div>
                        <Label>Rolle</Label>
                        <SuchAuswahl wert={rolle} onWaehlen={setRolle}
                            optionen={[
                                ...ROLLEN.map((r) => ({ wert: r.value, text: `${r.label} — ${r.was}` })),
                            ]}
                            testId="zugang-rolle"
                        />
                    </div>
                    <BereichWahl stamm={stamm} alle={alle} setAlle={setAlle}
                                 entitaeten={entitaeten} setEntitaeten={setEntitaeten}
                                 immobilien={immobilien} setImmobilien={setImmobilien}
                                 darfAlle={unbeschraenkt} />
                    <div>
                        <Label>Passwort <span className="text-rose-600">*</span></Label>
                        <Input type="password" value={passwort} onChange={(e) => setPasswort(e.target.value)}
                               autoComplete="new-password" data-testid="zugang-passwort" />
                        <div className="mt-1 text-[11px] text-gray-500">
                            Mindestens 10 Zeichen. Es wird bewusst kein Zufallspasswort erzeugt:
                            das müsste über einen Kanal weitergegeben werden, und der ist
                            meistens ein Chat.
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={speichern} disabled={busy || !email || passwort.length < 10} grund={sperre([busy, "Wird angelegt"], [!email, "Keine E-Mail eingegeben"], [passwort.length < 10, "Passwort hat weniger als 10 Zeichen"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="zugang-speichern">
                        Anlegen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
const PasswortDialog = ({ ziel, open, onOpenChange, onDone }) => {
    const { nutzer } = useAuth();
    const eigenes = ziel?.id === nutzer?.id;
    const [alt, setAlt] = useState("");
    const [neu, setNeu] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (!open) { setAlt(""); setNeu(""); } }, [open]);

    const speichern = async () => {
        setBusy(true);
        try {
            if (eigenes) await changeOwnPassword(alt, neu);
            else await setUserPassword(ziel.id, neu);
            toast.success("Passwort gesetzt");
            onDone();
            onOpenChange(false);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht gesetzt", { description: typeof d === "string" ? d : "Eingaben prüfen." });
        } finally { setBusy(false); }
    };

    if (!ziel) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{eigenes ? "Eigenes Passwort ändern" : "Passwort neu setzen"}</DialogTitle>
                    <DialogDescription>{ziel.email}</DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {eigenes && (
                        <div>
                            <Label>Bisheriges Passwort</Label>
                            <Input type="password" value={alt} onChange={(e) => setAlt(e.target.value)}
                                   autoComplete="current-password" data-testid="pw-alt" />
                            <div className="mt-1 text-[11px] text-gray-500">
                                Auch beim eigenen Zugang Pflicht: wer eine offene Sitzung an einem
                                fremden Rechner findet, soll damit nicht das Passwort übernehmen können.
                            </div>
                        </div>
                    )}
                    <div>
                        <Label>Neues Passwort</Label>
                        <Input type="password" value={neu} onChange={(e) => setNeu(e.target.value)}
                               autoComplete="new-password" data-testid="pw-neu" />
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={speichern}
                            disabled={busy || neu.length < 10 || (eigenes && !alt)} grund={sperre([busy, "Wird gesetzt"], [neu.length < 10, "Neues Passwort hat weniger als 10 Zeichen"], [eigenes && !alt, "Bisheriges Passwort fehlt"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="pw-speichern">
                        Setzen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
const BereichDialog = ({ ziel, onClose, onDone, stamm, unbeschraenkt }) => {
    const [alle, setAlle] = useState(false);
    const [entitaeten, setEntitaeten] = useState([]);
    const [immobilien, setImmobilien] = useState([]);
    const [telegram, setTelegram] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setAlle(!!ziel?.alle_bereiche);
        setEntitaeten(ziel?.entitaeten || []);
        setImmobilien(ziel?.immobilien || []);
        setTelegram(ziel?.telegram_id ? String(ziel.telegram_id) : "");
    }, [ziel]);

    const speichern = async () => {
        setBusy(true);
        try {
            await updateUser(ziel.id, {
                alle_bereiche: alle, entitaeten, immobilien,
                // 0 löst die Bindung — leeres Feld heißt in einem PATCH sonst
                // „nicht angefasst", und die Kennung ließe sich nie entfernen.
                telegram_id: telegram.trim() ? Number(telegram.trim()) : 0,
            });
            toast.success(`Bereich von ${ziel.email} geändert`);
            onDone();
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht geändert", { description: typeof d === "string" ? d : undefined });
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={!!ziel} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Bereich von {ziel?.email}</DialogTitle>
                    <DialogDescription>
                        Was dieser Zugang sieht. Alles außerhalb existiert für ihn nicht —
                        weder in Listen noch über einen direkten Aufruf.
                    </DialogDescription>
                </DialogHeader>
                <BereichWahl stamm={stamm} alle={alle} setAlle={setAlle}
                             entitaeten={entitaeten} setEntitaeten={setEntitaeten}
                             immobilien={immobilien} setImmobilien={setImmobilien}
                             darfAlle={unbeschraenkt} />
                <div>
                    <Label>Telegram-Kennung</Label>
                    <Input value={telegram} onChange={(e) => setTelegram(e.target.value)}
                           placeholder="z. B. 872230858" data-testid="zugang-telegram" />
                    <div className="mt-1 text-[11px] text-gray-500">
                        Der Bot arbeitet damit unter diesem Zugang — dieselbe Rolle,
                        derselbe Bereich. Ohne Eintrag antwortet er dieser Person nicht.
                        Die eigene Kennung nennt in Telegram der Bot <b>@userinfobot</b>.
                        Leer lassen löst die Bindung.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={speichern} disabled={busy} grund="Wird gespeichert" data-testid="bereich-speichern">
                        Speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


// ---------------------------------------------------------------------------
const Zugaenge = () => {
    const { nutzer, darfVerwalten, unbeschraenkt } = useAuth();
    const [liste, setListe] = useState([]);
    const [busy, setBusy] = useState(false);
    const [neuOffen, setNeuOffen] = useState(false);
    const [pwZiel, setPwZiel] = useState(null);
    const [bereichZiel, setBereichZiel] = useState(null);
    // Entitäten und Immobilien für die Auswahl. Kommen gefiltert an: ein
    // Verwalter kann nur weitergeben, was er selbst sieht — die Liste zeigt
    // also genau das, was das Backend auch annimmt.
    const [stamm, setStamm] = useState({ entities: [], props: [] });

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [users, entities, props] = await Promise.all([
                listUsers(), listEntities(), listProperties()]);
            setListe(users);
            setStamm({ entities, props });
        } finally { setBusy(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const rolleAendern = async (u, rolle) => {
        try {
            await updateUser(u.id, { role: rolle });
            toast.success(`${u.email} ist jetzt "${rolle}"`);
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht geändert", { description: typeof d === "string" ? d : undefined });
        }
    };

    const umschalten = async (u) => {
        try {
            await updateUser(u.id, { aktiv: !u.aktiv });
            toast.success(u.aktiv ? `${u.email} stillgelegt` : `${u.email} wieder aktiv`, {
                description: u.aktiv
                    ? "Wirkt sofort, auch für bereits offene Sitzungen."
                    : undefined,
            });
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht geändert", { description: typeof d === "string" ? d : undefined });
        }
    };

    const verwaltende = liste.filter((u) => u.role === "verwalten" && u.aktiv).length;

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Verwaltung</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="zugaenge-title">
                        Zugänge
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Wer hineinsehen darf und wer ändern darf. Alle Zugänge sehen denselben
                        Bestand ihres Bereichs — die Rolle entscheidet über das Ändern,
                        der Bereich über das Sehen.
                    </p>
                    <Seitenkopf id="seite.zugaenge" />
                </div>
                <div className="flex items-end gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} grund="Wird geladen" className="h-9">
                        <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
                    </Button>
                    {darfVerwalten && (
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9"
                                onClick={() => setNeuOffen(true)} data-testid="btn-zugang-neu">
                            <UserPlus size={14} className="mr-1.5" /> Zugang anlegen
                        </Button>
                    )}
                </div>
            </div>

            {verwaltende === 1 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-2"
                     data-testid="warnung-ein-verwalter">
                    <ShieldAlert size={16} className="text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-800">
                        <b className="text-amber-900">Nur ein verwaltender Zugang.</b> Geht dieses
                        Passwort verloren, kann niemand mehr Zugänge anlegen oder Rollen ändern —
                        das ließe sich nur noch direkt in der Datenbank reparieren. Ein zweiter
                        verwaltender Zugang ist die günstigere Versicherung.
                    </div>
                </div>
            )}

            {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left px-4 py-2.5">Zugang</th>
                            <th className="text-left px-4 py-2.5">Rolle</th>
                            <th className="text-left px-4 py-2.5">Bereich</th>
                            <th className="text-left px-4 py-2.5">Telegram</th>
                            <th className="text-left px-4 py-2.5">Zustand</th>
                            <th className="text-left px-4 py-2.5">Letzte Anmeldung</th>
                            <th className="px-4 py-2.5" />
                        </tr>
                    </thead>
                    <tbody>
                    {liste.map((u) => (
                        <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                            <td className="px-4 py-2.5">
                                <div className="font-medium text-slate-900">
                                    {u.name || u.email}
                                    {u.id === nutzer?.id && (
                                        <span className="ml-2 text-[11px] text-gray-400">(du)</span>
                                    )}
                                </div>
                                <div className="text-[11px] text-gray-400">{u.email}</div>
                            </td>
                            <td className="px-4 py-2.5">
                                {darfVerwalten ? (
                                    <SuchAuswahl wert={u.role} onWaehlen={(v) => rolleAendern(u, v)}
                                        optionen={[
                                            ...ROLLEN.map((r) => ({ wert: r.value, text: r.label })),
                                        ]}
                                        className="w-[140px]"
                                    />
                                ) : <RolleMerkmal rolle={u.role} />}
                            </td>
                            <td className="px-4 py-2.5">
                                {/* Ein Admin ist immer unbeschränkt — das kommt aus der
                                    Rolle, nicht aus einer Zuordnung, und ist deshalb auch
                                    nicht änderbar. */}
                                {u.role === "admin" ? (
                                    <span className="text-[11px] text-gray-500">alles (Admin)</span>
                                ) : darfVerwalten ? (
                                    <button className="text-[11px] text-slate-700 underline decoration-dotted"
                                            onClick={() => setBereichZiel(u)}
                                            data-testid={`bereich-${u.email}`}>
                                        {u.alle_bereiche ? "alles"
                                            : (u.entitaeten?.length || u.immobilien?.length)
                                                ? `${u.entitaeten?.length || 0} Entität(en), ${u.immobilien?.length || 0} Immobilie(n)`
                                                : "nichts zugeordnet"}
                                    </button>
                                ) : (
                                    <span className="text-[11px] text-gray-500">
                                        {u.alle_bereiche ? "alles" : `${(u.entitaeten?.length || 0) + (u.immobilien?.length || 0)} zugeordnet`}
                                    </span>
                                )}
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-gray-500 tabular-nums">
                                {u.telegram_id || "—"}
                            </td>
                            <td className="px-4 py-2.5">
                                {u.aktiv
                                    ? <span className="text-emerald-700 text-xs">aktiv</span>
                                    : <span className="text-rose-700 text-xs">stillgelegt</span>}
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs">
                                {u.letzte_anmeldung ? germanDate(u.letzte_anmeldung.slice(0, 10)) : "nie"}
                            </td>
                            <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                {(darfVerwalten || u.id === nutzer?.id) && (
                                    <Button variant="ghost" size="sm" className="h-7 px-2"
                                            onClick={() => setPwZiel(u)}
                                            aria-label="Passwort setzen" hilfe="aktion.setUserPassword"
                                            data-testid={`pw-${u.email}`}>
                                        <KeyRound size={14} />
                                    </Button>
                                )}
                                {darfVerwalten && u.id !== nutzer?.id && (
                                    <Button variant="ghost" size="sm" className="h-7 px-2"
                                            onClick={() => umschalten(u)}
                                            aria-label={u.aktiv ? "Stilllegen" : "Wieder aktivieren"}
                                            hilfe="aktion.updateUser.aktiv"
                                            data-testid={`aktiv-${u.email}`}>
                                        <Power size={14} className={u.aktiv ? "" : "text-rose-600"} />
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {liste.length === 0 && !busy && (
                        <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                            Keine Zugänge.
                        </td></tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div className="text-xs text-gray-400 leading-relaxed max-w-3xl">
                <b className="text-gray-500">Stilllegen statt löschen.</b> Ein stillgelegter Zugang
                kann sich nicht mehr anmelden — sofort, auch mit einem noch gültigen Token. Gelöscht
                würde er dagegen seine Spuren namenlos machen. Löschen bleibt über die API möglich,
                für Zugänge, die aus Versehen entstanden sind.
            </div>

            <NeuDialog open={neuOffen} onOpenChange={setNeuOffen} onDone={load}
                       stamm={stamm} unbeschraenkt={unbeschraenkt} />
            <BereichDialog ziel={bereichZiel} onClose={() => setBereichZiel(null)}
                           onDone={load} stamm={stamm} unbeschraenkt={unbeschraenkt} />
            <PasswortDialog ziel={pwZiel} open={!!pwZiel}
                            onOpenChange={(o) => !o && setPwZiel(null)} onDone={load} />
        </div>
    );
};

export default Zugaenge;
